import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Gauge, CircleDollarSign, Zap, Shield, Wifi, Headphones } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/lib/hooks";

const formatPrice = (price) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);

const featureIcons = [Zap, Shield, Wifi, Headphones, Gauge, CircleDollarSign];

export default function PackageDetail() {
  const { speed } = useParams();
  const { t, ml } = useLang();
  const { data: packages = [], isLoading } = useContent("/content/packages");

  const pkg = !isLoading ? packages.find((p) => String(p.speed) === speed) : null;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-zinc-500" data-testid="package-detail-loading">
        {t("common.loading")}
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="package-detail-notfound">
        <p className="font-heading text-3xl font-black tracking-tighter text-zinc-950">{t("packageDetail.notFound")}</p>
        <p className="mt-3 text-zinc-500">{t("packageDetail.notFoundDesc")}</p>
        <Link
          to="/tarifeler"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4F46E5] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#4338CA]"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t("packageDetail.back")}
        </Link>
      </div>
    );
  }

  const features = ml(pkg.features);
  const featureList = Array.isArray(features) ? features : [];

  return (
    <div data-testid={`package-detail-${pkg.speed}`}>
      <Link
        to="/tarifeler"
        data-testid="package-detail-back"
        className="mx-auto mt-28 flex max-w-7xl items-center gap-2 px-4 text-sm font-bold text-[#4F46E5] hover:underline sm:px-6 lg:px-8"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
        {t("packageDetail.back")}
      </Link>

      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 pt-8 pb-20 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-[#101436] via-[#1e1b4b] to-[#312e81]">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10 lg:p-16">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#ff9800]">
                {pkg.name}
              </span>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-7xl font-extrabold tracking-tighter text-white sm:text-8xl">
                  {pkg.speed}
                </span>
                <span className="text-xl font-bold text-[#ff9800]">{t("packages.mbps")}</span>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold tracking-tight text-white">
                  ₺{formatPrice(pkg.price)}
                </span>
                <span className="text-lg text-zinc-400">{t("packages.perMonth")}</span>
              </div>
              <p className="mt-3 text-sm text-zinc-400">{t("packageDetail.note")}</p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/iletisim"
                  data-testid={`package-detail-apply-${pkg.speed}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#ff9800] px-7 py-3.5 text-sm font-bold text-[#1d1d1f] transition-colors hover:bg-[#f57c00]"
                >
                  {t("packageDetail.applyNow")}
                  <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Link>
                <a
                  href="tel:+903247141704"
                  data-testid={`package-detail-call-${pkg.speed}`}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white/20 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
                >
                  {t("packageDetail.contactUs")}
                </a>
              </div>
            </div>
            <div className="relative hidden items-center justify-center p-16 lg:flex">
              <div className="absolute -bottom-16 -end-16 h-56 w-56 rounded-full bg-[#ff9800]/10 blur-3xl" />
              <div className="absolute -top-16 -start-16 h-56 w-56 rounded-full bg-[#7C3AED]/20 blur-3xl" />
              <div className="relative grid grid-cols-2 gap-4">
                <div className="flex flex-col items-center rounded-2xl bg-white/5 p-6 backdrop-blur-sm">
                  <Gauge className="h-8 w-8 text-[#ff9800]" />
                  <span className="mt-3 font-display text-3xl font-extrabold text-white">{pkg.speed}</span>
                  <span className="mt-1 text-xs text-zinc-400">{t("packages.mbps")}</span>
                </div>
                <div className="flex flex-col items-center rounded-2xl bg-white/5 p-6 backdrop-blur-sm">
                  <Shield className="h-8 w-8 text-[#ff9800]" />
                  <span className="mt-3 font-display text-3xl font-extrabold text-white">∞</span>
                  <span className="mt-1 text-xs text-zinc-400">Sınırsız</span>
                </div>
                <div className="flex flex-col items-center rounded-2xl bg-white/5 p-6 backdrop-blur-sm">
                  <Wifi className="h-8 w-8 text-[#ff9800]" />
                  <span className="mt-3 font-display text-3xl font-extrabold text-white">AIR</span>
                  <span className="mt-1 text-xs text-zinc-400">Fiber</span>
                </div>
                <div className="flex flex-col items-center rounded-2xl bg-white/5 p-6 backdrop-blur-sm">
                  <CircleDollarSign className="h-8 w-8 text-[#ff9800]" />
                  <span className="mt-3 font-display text-3xl font-extrabold text-white">₺{formatPrice(pkg.price)}</span>
                  <span className="mt-1 text-xs text-zinc-400">{t("packages.perMonth")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">
            {t("packageDetail.featuresTitle")}
          </h2>
          <p className="mt-3 text-zinc-500">{t("packageDetail.featuresDesc")}</p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {featureList.map((feature, i) => {
            const Icon = featureIcons[i % featureIcons.length];
            return (
              <div
                key={i}
                data-testid={`package-detail-feature-${i}`}
                className="flex items-start gap-4 rounded-xl border border-zinc-200 bg-white p-6 transition-shadow hover:shadow-md"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#f3f0ff] text-[#4F46E5]">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{feature}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SPEED & PRICE DETAILS */}
      <section className="bg-zinc-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div
              data-testid={`package-detail-speed-${pkg.speed}`}
              className="rounded-2xl border border-zinc-200 bg-white p-8"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#f3f0ff] text-[#4F46E5]">
                  <Gauge className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-lg font-bold text-zinc-950">{t("packageDetail.speedTitle")}</p>
                  <p className="text-sm text-zinc-500">{t("packageDetail.speedDesc")}</p>
                </div>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-5xl font-extrabold tracking-tighter text-zinc-950">
                  {pkg.speed}
                </span>
                <span className="text-lg font-bold text-[#4F46E5]">{t("packages.mbps")}</span>
              </div>
            </div>
            <div
              data-testid={`package-detail-price-${pkg.speed}`}
              className="rounded-2xl border border-zinc-200 bg-white p-8"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#f3f0ff] text-[#4F46E5]">
                  <CircleDollarSign className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-lg font-bold text-zinc-950">{t("packageDetail.priceTitle")}</p>
                  <p className="text-sm text-zinc-500">{t("packageDetail.priceDesc")}</p>
                </div>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-5xl font-extrabold tracking-tighter text-zinc-950">
                  ₺{formatPrice(pkg.price)}
                </span>
                <span className="text-lg text-zinc-400">{t("packages.perMonth")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-gradient-to-br from-[#101436] via-[#1e1b4b] to-[#312e81] p-10 text-center md:p-16">
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {pkg.speed} Mbps {t("packages.mbps")} — {pkg.name}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-zinc-300">
            {t("packageDetail.note")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/iletisim"
              data-testid={`package-detail-bottom-apply-${pkg.speed}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#ff9800] px-8 py-4 text-sm font-bold text-[#1d1d1f] transition-colors hover:bg-[#f57c00]"
            >
              {t("packageDetail.applyNow")}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
            <Link
              to="/tarifeler"
              data-testid={`package-detail-compare-${pkg.speed}`}
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/20 px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              {t("home.allPackages")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
